<template>
	<section data-component="_image-set" :class="`layout-${resolvedLayout}`">
		<div v-if="resolvedLayout === 'singleFull'" class="images-row">
			<ImageSetMedia
				v-for="{ index, image } in singleImages"
				:key="imageKey('singleFull', 'single', index, image)"
				:class="['desktop', 'image', imageClass(index)]"
				:image="image"
			/>
		</div>

		<div v-else-if="resolvedLayout === 'threeTwoOne'" class="g-row images-row">
			<div class="g-col xxl-12 xs-24">
				<ImageSetMedia
					v-for="{ index, image } in leftColumnImages"
					:key="imageKey('threeTwoOne', 'left', index, image)"
					:class="['desktop', 'image', imageClass(index)]"
					:image="image"
				/>
			</div>
			<div class="g-col xxl-12 xs-24">
				<ImageSetMedia
					v-for="{ index, image } in rightColumnImages"
					:key="imageKey('threeTwoOne', 'right', index, image)"
					:class="['desktop', 'image', imageClass(index)]"
					:image="image"
				/>
			</div>
		</div>

		<div v-else-if="resolvedLayout === 'threeStackedOffset'" class="g-row images-row">
			<div class="g-col xxl-24 xs-24 image-row row-one">
				<ImageSetMedia
					v-for="{ index, image } in rowOneImages"
					:key="imageKey('threeStackedOffset', 'rowOne', index, image)"
					:class="['desktop', 'image', imageClass(index)]"
					:image="image"
				/>
			</div>
			<div class="g-col xxl-24 xs-24 image-row row-two">
				<ImageSetMedia
					v-for="{ index, image } in rowTwoImages"
					:key="imageKey('threeStackedOffset', 'rowTwo', index, image)"
					:class="['desktop', 'image', imageClass(index)]"
					:image="image"
				/>
			</div>
			<div class="g-col xxl-24 xs-24 image-row row-three">
				<ImageSetMedia
					v-for="{ index, image } in rowThreeImages"
					:key="imageKey('threeStackedOffset', 'rowThree', index, image)"
					:class="['desktop', 'image', imageClass(index)]"
					:image="image"
				/>
			</div>
		</div>

		<div v-else class="g-row images-row">
			<div class="g-col xxl-12 xs-24 left-column">
				<ImageSetMedia
					v-for="{ index, image } in leftColumnImages"
					:key="imageKey('fourTwoByTwo', 'left', index, image)"
					:class="['desktop', 'image', imageClass(index)]"
					:image="image"
				/>
			</div>
			<div class="g-col xxl-12 xs-24 right-column">
				<ImageSetMedia
					v-for="{ index, image } in rightColumnImages"
					:key="imageKey('fourTwoByTwo', 'right', index, image)"
					:class="['desktop', 'image', imageClass(index)]"
					:image="image"
				/>
			</div>
		</div>
	</section>
</template>

<script setup>
import ImageSetMedia from './ImageSetMedia.vue'

const DEFAULT_LAYOUT = 'threeTwoOne'
const LAYOUT_CONFIG = {
	singleFull: {
		required: 1,
		min: 1,
		max: 1,
		slotMap: {
			single: [0],
		},
	},
	threeTwoOne: {
		required: 3,
		min: 3,
		max: 3,
		slotMap: {
			left: [0, 1],
			right: [2],
		},
	},
	threeStackedOffset: {
		required: 3,
		min: 3,
		max: 3,
		slotMap: {
			rowOne: [0],
			rowTwo: [1],
			rowThree: [2],
		},
	},
	fourTwoByTwo: {
		required: 4,
		min: 4,
		max: 4,
		slotMap: {
			left: [0, 1],
			right: [2, 3],
		},
	},
}

const props = defineProps({
	layout: { type: String, default: DEFAULT_LAYOUT },
	images: { type: Array, default: () => [] },
})

const emittedWarnings = new Set()

const warnDevOnce = (key, message) => {
	if (!import.meta.dev || emittedWarnings.has(key)) return
	emittedWarnings.add(key)
	console.warn(`[ImageSet] ${message}`)
}

const resolvedLayout = computed(() => {
	if (LAYOUT_CONFIG[props.layout]) return props.layout

	warnDevOnce(
		`layout-${props.layout}`,
		`Unknown layout "${props.layout}". Falling back to "${DEFAULT_LAYOUT}".`,
	)
	return DEFAULT_LAYOUT
})

const layoutDefinition = computed(() => LAYOUT_CONFIG[resolvedLayout.value])

const getSlotImages = (slotName) => {
	const slotMap = layoutDefinition.value.slotMap
	const indices = slotMap[slotName] || []

	return indices
		.map((index) => ({
			index,
			image: props.images[index],
		}))
		.filter(({ image }) => hasMediaSource(image))
}

const singleImages = computed(() => getSlotImages('single'))
const leftColumnImages = computed(() => getSlotImages('left'))
const rightColumnImages = computed(() => getSlotImages('right'))
const rowOneImages = computed(() => getSlotImages('rowOne'))
const rowTwoImages = computed(() => getSlotImages('rowTwo'))
const rowThreeImages = computed(() => getSlotImages('rowThree'))

watchEffect(() => {
	const { min, max, required } = layoutDefinition.value
	const imageCount = props.images.length
	const layout = resolvedLayout.value

	if (imageCount < min) {
		warnDevOnce(
			`count-low-${layout}-${imageCount}`,
			`Layout "${layout}" expects ${required} image(s), received ${imageCount}. Rendering available slots only.`,
		)
	}

	if (imageCount > max) {
		warnDevOnce(
			`count-high-${layout}-${imageCount}`,
			`Layout "${layout}" supports up to ${max} image(s), received ${imageCount}. Extra images will be ignored.`,
		)
	}
})

const imageClass = (index) => `image-${index + 1}`
const hasMediaSource = (image) => Boolean(image?.src || image?.sources?.length)
const mediaIdentifier = (image) => image?.src || image?.url || image?.sources?.[0]?.url || image?.alt || image?.type
const imageKey = (layout, slotName, index, image) => `${layout}-${slotName}-${index}-${mediaIdentifier(image)}`
</script>

<style lang="scss" scoped>
[data-component='_image-set'] {
	position: relative;
	width: 100%;

	.images-row {
		width: 100%;
	}

	.image {
		display: block;
		width: 100%;
	}

	&.layout-singleFull {
		width: var(--100vw);
		margin-left: calc((100% - var(--100vw)) / 2);

		.image-1 {
			width: 100%;
		}
	}

	&.layout-threeTwoOne {
		.image-1 {
			width: 100%;
		}

		.image-2 {
			width: column-width(5);
			margin-top: var(--grid-gap);
			margin-left: auto;
		}

		.image-3 {
			width: 100%;
			margin-top: rs(650);
		}
	}

	&.layout-threeStackedOffset {
		.image-row + .image-row {
			margin-top: var(--grid-gap);
		}

		.row-one .image-1 {
			width: 100%;
		}

		.row-two .image-2 {
			width: column-width(14);
			// margin-left: auto;
		}

		.row-three .image-3 {
			width: column-width(10);
			margin-top: rs(93, 10);
			margin-left: column-width(12);
		}
	}

	&.layout-fourTwoByTwo {
		.left-column .image-1 {
			width: column-width(4);
			margin-left: auto;
			margin-right: column-width(1, true);
		}

		.left-column .image-2 {
			width: column-width(10);
			margin-top: var(--grid-gap);
			margin-left: column-width(1, true);
		}

		.right-column .image-3 {
			width: column-width(10);
			margin-left: column-width(1, true);
		}

		.right-column .image-4 {
			width: column-width(11);
			margin-top: rs(160, 20);
			margin-left: calc(column-width(1, true) * -1);
		}
	}

	@include max-width($xs) {
		.images-row {
			display: flex;
			flex-direction: column;
			gap: var(--grid-gap);
		}

		.images-row > .g-col {
			width: 100%;
			max-width: 100%;
			flex: 0 0 100%;
		}

		.image-row + .image-row {
			margin-top: 0 !important;
		}

		.image {
			width: 100% !important;
			margin: 0 !important;
		}

		.image + .image {
			margin-top: var(--grid-gap) !important;
		}
	}
}
</style>
